import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  useTheme,
  Snackbar,
  Avatar,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  DateRange as DateRangeIcon,
} from "@mui/icons-material";
import { ModernTextField } from "../common/StyledComponents";
import ErrorAlert from "../common/ErrorAlert";
import { useError } from "../../error/useError";
import { useUserProfileContext } from "../../contexts/ProfileContext";
import { UserProfileSettings } from "../../models/user/UserProfile";

type DateFormatOption = {
  label: string;
  value: string;
  example: string;
};

const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  { label: "MM/DD/YYYY", value: "MM/DD/YYYY", example: "12/31/2025" },
  { label: "DD/MM/YYYY", value: "DD/MM/YYYY", example: "31/12/2025" },
  { label: "YYYY-MM-DD", value: "YYYY-MM-DD", example: "2025-12-31" },
];

const AccountSettingsSection: React.FC = () => {
  const theme = useTheme();

  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const { error, helpText, setError, clearError, handleError } = useError();

  const [dateFormat, setDateFormat] = useState<string>("");

  const {
    userProfileSettings,
    refetchProfile,
    updateProfileSettings,
    isSavingProfileSettings,
  } = useUserProfileContext();

  useEffect(() => {
    if (userProfileSettings) {
      setDateFormat(userProfileSettings.dateFormat);
    }
  }, [userProfileSettings]);

  const handleSave = () => {
    if (!dateFormat.trim()) {
      setError("Date format is required");
      return;
    }

    const updatedProfileSettings: UserProfileSettings = {
      profileId: userProfileSettings?.profileId,
      dateFormat,
    };

    updateProfileSettings(updatedProfileSettings, {
      onSuccess: () => {
        setSuccess("Account settings updated successfully!");
        setEditing(false);
        refetchProfile();
      },
      onError: (error) => {
        handleError(error);
      },
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setDateFormat(userProfileSettings?.dateFormat || "");
    clearError();
  };

  const selectedOption = DATE_FORMAT_OPTIONS.find(
    (opt) => opt.value === dateFormat
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              backgroundColor: theme.palette.info.main + "20",
              color: theme.palette.info.main,
            }}
          >
            <SettingsIcon />
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Account Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your account settings
            </Typography>
          </Box>

          {!editing ? (
            <Tooltip title="Edit settings">
              <IconButton
                onClick={() => setEditing(true)}
                sx={{
                  backgroundColor: theme.palette.info.main + "10",
                  color: theme.palette.info.main,
                  "&:hover": {
                    backgroundColor: theme.palette.info.main + "20",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s",
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Save changes">
                <IconButton
                  onClick={handleSave}
                  disabled={isSavingProfileSettings}
                  sx={{
                    backgroundColor: theme.palette.success.main + "10",
                    color: theme.palette.success.main,
                    "&:hover": {
                      backgroundColor: theme.palette.success.main + "20",
                      transform: "scale(1.05)",
                    },
                    "&:disabled": {
                      backgroundColor: theme.palette.grey[200],
                      color: theme.palette.grey[400],
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Cancel">
                <IconButton
                  onClick={handleCancel}
                  sx={{
                    backgroundColor: theme.palette.error.main + "10",
                    color: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: theme.palette.error.main + "20",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <ErrorAlert
        error={error}
        helpText={helpText}
        onClear={clearError}
        onRetry={() => refetchProfile()}
        title="Account Settings Error"
      />

      <Grid container spacing={3}>
        {/* Date Format Setting */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.info.main + "15",
                    color: theme.palette.info.main,
                    width: 36,
                    height: 36,
                  }}
                >
                  <DateRangeIcon />
                </Avatar>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Date Format
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose how dates are displayed across the app
                  </Typography>
                </Box>
              </Box>

              <ModernTextField
                select
                fullWidth
                label="Preferred date format"
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value)}
                disabled={!editing}
                helperText={
                  editing
                    ? "Select your preferred date format"
                    : "Click edit to change date format"
                }
              >
                {DATE_FORMAT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label} — {option.example}
                  </MenuItem>
                ))}
              </ModernTextField>

              {selectedOption && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Example
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedOption.example}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Box>
  );
};

export default AccountSettingsSection;
